import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * How long each API call takes as this device sees it, next to the server's own
 * share (its Server-Timing header). The server's route table stops the clock
 * when Rails returns, so queueing, the tunnel and the network never showed up
 * anywhere; the difference between these two numbers is exactly that.
 *
 * Batched and posted to /api/v1/client_timings, which keeps only per-hour
 * aggregates. No user or session id is sent.
 */

export interface TimingSample {
  /** HTTP method */
  m: string;
  /** Path without origin or query, or a synthetic key such as R2_BUNDLE_KEY */
  p: string;
  /** Milliseconds from sending to the response (or failure) arriving */
  c: number;
  /** Server-Timing app time, when the response carried one */
  a: number | null;
  /** Server-Timing queue time, when the response carried one */
  q: number | null;
  /** HTTP status, or 0 when no response arrived (timeout, network error) */
  st: number;
  /** Epoch ms the request started */
  at: number;
}

type Finish = (status: number, serverTiming?: unknown) => void;

export const SAMPLE_RATE = 1.0;
/** The chapter bundle fetched straight from R2, which never passes through Rails. */
export const R2_BUNDLE_KEY = 'r2:chapter_bundle';

export const REPORT_PATH = '/api/v1/client_timings';
const BUFFER_CAP = 200;
const FLUSH_AT = 50;
const MAX_BATCH = 100;
const FLUSH_INTERVAL_MS = 60_000;
// Mirrors ClientTimingIngest::NOISE_ROUTES. It only picks `prev`, so a miss
// costs one link across a batch boundary and nothing else.
const NOISE_PATH = /^\/api\/(presence\/heartbeats|app_settings|v1\/(analytics\/ingest|client_errors|push_tokens))$/;

const buffer: TimingSample[] = [];
let prev: Pick<TimingSample, 'm' | 'p' | 'at'> | null = null;
let reporter: AxiosInstance | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
const inFlight = new WeakMap<InternalAxiosRequestConfig, Finish>();

/** Reads `app` and `queue` durations; ignores any other entry (Cloudflare adds its own). */
export function parseServerTiming(header: unknown): { a: number | null; q: number | null } {
  const out: { a: number | null; q: number | null } = { a: null, q: null };
  if (typeof header !== 'string') return out;
  for (const entry of header.split(',')) {
    const [name, ...params] = entry.split(';').map((part) => part.trim());
    const dur = params.find((param) => param.startsWith('dur='));
    const ms = dur === undefined ? NaN : Number(dur.slice(4));
    if (!Number.isFinite(ms)) continue;
    if (name === 'app') out.a = ms;
    if (name === 'queue') out.q = ms;
  }
  return out;
}

/** Starts a clock for one request; call the result when it settles. */
export function timeRequest(m: string, p: string): Finish {
  if (Math.random() >= SAMPLE_RATE) return () => {};
  const started = performance.now();
  const at = Date.now();
  return (st, serverTiming) => {
    try {
      const { a, q } = parseServerTiming(serverTiming);
      buffer.push({ m, p, c: Math.round(performance.now() - started), a, q, st, at });
      if (buffer.length > BUFFER_CAP) buffer.shift();
      if (buffer.length >= FLUSH_AT) flushClientTimings();
      else if (timer === null) timer = setTimeout(flushClientTimings, FLUSH_INTERVAL_MS);
    } catch {
      // Measuring must never touch the request it measured.
    }
  };
}

/** Fire-and-forget: a failed post is dropped, never retried. */
export function flushClientTimings(): void {
  try {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (!reporter || buffer.length === 0) return;

    const samples = buffer.splice(0, MAX_BATCH).sort((x, y) => x.at - y.at);
    const body = { prev, samples };
    for (let i = samples.length - 1; i >= 0; i--) {
      if (!NOISE_PATH.test(samples[i].p)) {
        prev = { m: samples[i].m, p: samples[i].p, at: samples[i].at };
        break;
      }
    }
    reporter.post(REPORT_PATH, body).catch(() => {});
  } catch {
    // Reporting is best-effort by definition.
  }
}

/** Times every API call made through `api`, and posts the batches through it. */
export function installClientTiming(api: AxiosInstance): void {
  reporter = api;

  api.interceptors.request.use((config) => {
    const path = (config.url ?? '').replace(/^https?:\/\/[^/]+/, '').split('?')[0];
    if (path.startsWith('/api/') && path !== REPORT_PATH) {
      inFlight.set(config, timeRequest((config.method ?? 'get').toUpperCase(), path));
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => {
      // No request object means it was answered on the device (the mobile
      // offline cache), so there is no network time to report.
      if (response.request) inFlight.get(response.config)?.(response.status, response.headers['server-timing']);
      return response;
    },
    (error: AxiosError) => {
      // A cancelled query (navigating away) is not a slow or failed request.
      if (error.config && error.code !== 'ERR_CANCELED') {
        inFlight.get(error.config)?.(error.response?.status ?? 0, error.response?.headers?.['server-timing']);
      }
      return Promise.reject(error);
    },
  );

  // Web only: the tab is going away or into the background. React Native has
  // no document, and core has no app-state hook, so mobile relies on the timer.
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushClientTimings();
    });
    window.addEventListener('pagehide', flushClientTimings);
  }
}
